import type { Glocke, Glockeneintrag } from './api/uebersicht';
import { alsTag, alsZeitpunkt, heute } from './datum';
import type { Aufklappeintrag } from './komponenten/Aufklapper';
import { Sinnbild } from './komponenten/Sinnbild';

/** Wohin ein Eintrag der Glocke führt — die Masken kennt nur die Arbeitsfläche. */
export type Glockenwege = {
  /** Die Wiedervorlage in „Meine Wiedervorlagen", geöffnet. */
  readonly zurWiedervorlage: (id: string) => void;
  /** „Alle meine Wiedervorlagen" — fehlt ohne `kunde/sehen`. */
  readonly zuMeinenWiedervorlagen?: (() => void) | undefined;
  readonly zumTermin: (id: string) => void;
  readonly zumAngebot: (id: string) => void;
  /** SUP-25 (Auftrag CRM-178): der Vorgang im Posteingang, im Reiter seines Postfachs. */
  readonly zumVorgang?: ((id: string, postfach: string) => void) | undefined;
};

const UHRZEIT = new Intl.DateTimeFormat('de-DE', { timeStyle: 'short', timeZone: 'Europe/Berlin' });

/** „überfällig seit 08.10.2026", „heute", „Erinnerung, fällig 14.10.2026". */
function wannFaellig(e: Extract<Glockeneintrag, { art: 'wiedervorlage' }>, tag: string): string {
  if (e.ueberfaellig) return `überfällig seit ${alsTag(e.faelligAm) ?? e.faelligAm}`;
  if (e.faelligAm === tag) return 'heute';
  return `Erinnerung, fällig ${alsTag(e.faelligAm) ?? e.faelligAm}`;
}

/**
 * SUP-25, SUP-26 (Auftrag CRM-178): „Vorgang SV-2026-00012 · eskaliert ·
 * überfällig seit 10.10.2026, 14:00 · Vertretung für Anna Abwesend".
 */
function vorgangszeile(e: Extract<Glockeneintrag, { art: 'supportvorgang' }>): string {
  const faellig = e.faelligAm === null ? null : (alsZeitpunkt(e.faelligAm) ?? e.faelligAm);
  return [
    `Vorgang ${e.nummer}`,
    e.eskaliert ? 'eskaliert' : null,
    faellig === null ? null : e.ueberfaellig ? `überfällig seit ${faellig}` : `fällig ${faellig}`,
    e.vertretungFuer === null ? null : `Vertretung für ${e.vertretungFuer}`,
  ].filter((t) => t !== null).join(' · ');
}

function eintrag(e: Glockeneintrag, wege: Glockenwege, tag: string): Aufklappeintrag {
  if (e.art === 'supportvorgang') {
    const zeile = vorgangszeile(e);
    return {
      schluessel: `supportvorgang-${e.id}`,
      text: e.betreff,
      unterzeile: zeile,
      vorne: <Sinnbild name="envelope-simple" gewicht="light" />,
      vorlesen: `${e.betreff}, ${zeile}`,
      beiWahl: () => wege.zumVorgang?.(e.id, e.postfach),
    };
  }
  if (e.art === 'wiedervorlage') {
    const wann = wannFaellig(e, tag);
    return {
      schluessel: `wiedervorlage-${e.id}`,
      text: e.betreff,
      // KUN-15: Kunde, Betreff und Tag je Eintrag.
      unterzeile: `${e.kunde?.name ?? 'ohne Kunde'} · ${wann}`,
      vorne: <Sinnbild name="clock-counter-clockwise" gewicht="light" />,
      vorlesen: `Wiedervorlage ${e.betreff}, ${e.kunde?.name ?? 'ohne Kunde'}, ${wann}`,
      beiWahl: () => wege.zurWiedervorlage(e.id),
    };
  }
  if (e.art === 'angebot') {
    // ANG-01, ANG-17: das eigene Angebot, dessen Gültigkeit heute endet.
    const wann = e.ueberfaellig ? `abgelaufen seit ${alsTag(e.gueltigBis) ?? e.gueltigBis}` : 'gültig bis heute';
    return {
      schluessel: `angebot-${e.id}`,
      text: e.betreff,
      unterzeile: `${e.kunde.name} · Angebot ${e.nummer} · ${wann}`,
      vorne: <Sinnbild name="file-text" gewicht="light" />,
      vorlesen: `Angebot ${e.nummer} ${e.betreff}, ${e.kunde.name}, ${wann}`,
      beiWahl: () => wege.zumAngebot(e.id),
    };
  }
  // BUC-76: die eigene Option, deren Frist heute endet.
  const frist = `läuft heute ${UHRZEIT.format(new Date(e.fristBis))} Uhr ab`;
  const termin = `${e.plaetze === 1 ? '1 Platz' : `${String(e.plaetze)} Plätze`} · ${alsZeitpunkt(e.beginn) ?? e.beginn}`;
  return {
    schluessel: `option-${e.id}`,
    text: `Option ${e.produkt}`,
    unterzeile: `${termin} · ${frist}`,
    vorne: <Sinnbild name="calendar-blank" gewicht="light" />,
    vorlesen: `Option ${e.produkt}, ${termin}, ${frist}`,
    beiWahl: () => wege.zumTermin(e.termin),
  };
}

/**
 * Die Einträge der Glocke (Auftrag CRM-170, ÄA-70): jeder eigene fällige
 * Eintrag einzeln, dazu am Ende „Alle meine Wiedervorlagen", abgesetzt durch
 * eine Linie. Der Weg dorthin steht auch ohne fällige Einträge da — die Glocke
 * ist der kürzeste Weg zur eigenen Liste.
 */
export function glockeneintraege(glocke: Glocke, wege: Glockenwege): readonly Aufklappeintrag[] {
  const tag = heute();
  const einzeln = glocke.eintraege.map((e) => eintrag(e, wege, tag));
  if (wege.zuMeinenWiedervorlagen === undefined) return einzeln;
  const letzter = einzeln.at(-1);
  const rest = glocke.faellig - glocke.eintraege.length;
  return [
    ...einzeln.slice(0, -1),
    ...(letzter === undefined ? [] : [{ ...letzter, abgesetzt: true }]),
    {
      schluessel: 'alle-wiedervorlagen',
      text: 'Alle meine Wiedervorlagen',
      ...(rest > 0 ? { zusatz: `${String(rest)} weitere` } : {}),
      beiWahl: wege.zuMeinenWiedervorlagen,
    },
  ];
}
